import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { publicOffer } from '../features/offer/publicOffer';

const hasPublicPlus = publicOffer.isPublic && publicOffer.priceMxn > 0;
const assistantStatus =
  publicOffer.assistantStatus === 'available'
    ? 'Disponible'
    : publicOffer.assistantStatus === 'beta'
      ? 'Beta'
      : 'Próximamente';
const canUseAssistant = publicOffer.assistantStatus !== 'coming_soon';

export function LandingPage() {
  return (
    <main className="min-h-full bg-[#faf8f5] pb-[68px] text-slate-900 md:pb-0">
      <header className="border-b border-[#e8e2d9] bg-[#faf8f5]/95 backdrop-blur">
        <div className="mx-auto flex min-h-[76px] max-w-[1440px] items-center justify-between gap-3 px-5 sm:px-8 xl:px-[120px]">
          <Brand />
          <nav aria-label="Principal" className="hidden items-center gap-1 md:flex">
            <Nav href="#como-funciona">Cómo funciona</Nav>
            <Nav href="#asistente">Asistente</Nav>
            {hasPublicPlus && <Nav href="#plus">Plus</Nav>}
            <Link to="/privacidad" className="nav-link">
              Privacidad
            </Link>
            <span className="mx-2 h-6 w-px bg-[#e2dbd1]" />
            <Link to="/login" className="nav-link font-semibold text-slate-900">
              Iniciar sesión
            </Link>
            <Link to="/registro" className="button button-primary ml-1 min-h-11 rounded-xl px-5">
              Crear cuenta gratis
            </Link>
          </nav>
          <div className="flex items-center gap-1 md:hidden">
            <Link
              to="/privacidad"
              className="inline-flex min-h-11 items-center px-2 text-sm text-slate-700 underline"
            >
              Privacidad
            </Link>
            <Link
              to="/login"
              className="inline-flex min-h-11 items-center px-2 text-sm font-semibold"
            >
              Entrar
            </Link>
          </div>
        </div>
      </header>

      <section className="mx-auto grid max-w-[1440px] gap-12 px-5 py-14 sm:px-8 lg:grid-cols-[minmax(0,592px)_minmax(0,1fr)] lg:items-center lg:gap-[88px] lg:px-[120px] lg:py-[92px]">
        <div>
          <p className="mb-5 font-mono text-xs font-semibold uppercase tracking-[.16em] text-slate-600">
            Tus finanzas con ayuda de IA
          </p>
          <h1 className="max-w-[620px] text-[38px] font-bold leading-[1.05] tracking-[-.035em] sm:text-5xl lg:text-[60px]">
            Registra tus gastos con una frase. Decide con números claros.
          </h1>
          <p className="mt-6 max-w-[540px] text-[17px] leading-7 text-slate-600 sm:text-xl sm:leading-8">
            Escribe una compra como la contarías. El asistente prepara el registro y te muestra cómo
            cambiaría tu saldo o la deuda de tu tarjeta. Tú revisas y confirmas.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/registro"
              className="button button-primary min-h-14 rounded-[14px] px-7 text-[17px]"
            >
              Probar gratis
            </Link>
            <a
              href="#asistente"
              className="button button-secondary min-h-14 rounded-[14px] border-[#dcd5ca] px-6 text-[17px]"
            >
              Ver un ejemplo <Icon name="down" className="h-4 w-4 text-slate-500" />
            </a>
          </div>
          <ul className="mt-5 flex flex-col gap-2 text-sm font-semibold leading-6 text-green-700 sm:flex-row sm:flex-wrap sm:gap-x-5">
            <TrustNote>Empieza con tus saldos de hoy.</TrustNote>
            <TrustNote>No necesitas conectar tu banco.</TrustNote>
            <TrustNote>Tú confirmas cada cambio.</TrustNote>
          </ul>
        </div>
        <HeroPreview />
      </section>

      <AssistantShowcase />

      <section className="border-y border-[#e8e2d9] bg-white px-5 py-14 sm:px-8 lg:px-[120px] lg:py-24">
        <div className="mx-auto max-w-[1200px]">
          <h2 className="section-title">Controla tus gastos y decide mejor</h2>
          <p className="mt-3 max-w-[680px] text-[17px] leading-7 text-slate-600 sm:text-lg">
            Reúne lo que tienes, lo que debes y lo que viene. Así puedes decidir con tus propios
            números.
          </p>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:mt-14 lg:grid-cols-4 lg:gap-6">
            <Benefit icon="wallet" tone="green" title="Controla tus gastos">
              Ve en qué gastas y registra lo cotidiano con tus palabras.
            </Benefit>
            <Benefit icon="calendar" tone="amber" title="Prepárate para tus pagos">
              Reúne renta, mensualidades, pagos de tarjeta y otras deudas, sin contarlos dos veces.
            </Benefit>
            <Benefit icon="chart" tone="blue" title="Conoce tu patrimonio">
              Ve lo que tienes menos lo que debes. Ese es tu patrimonio.
            </Benefit>
            <Benefit icon="target" tone="green" title="Piensa antes de comprar">
              Mira cómo afectaría la compra tu presupuesto, tu ahorro y tus próximos pagos. Tú
              decides.
            </Benefit>
          </div>
        </div>
      </section>

      <section
        id="como-funciona"
        className="mx-auto max-w-[1440px] px-5 py-14 sm:px-8 lg:px-[120px] lg:py-24"
      >
        <p className="font-mono text-xs font-semibold uppercase tracking-[.16em] text-slate-600">
          Cómo funciona
        </p>
        <h2 className="mt-4 section-title">Empieza con lo que sabes hoy</h2>
        <div className="mt-10 grid gap-5 md:grid-cols-3 lg:mt-14 lg:gap-6">
          <Step number="01" title="Empieza con tus saldos actuales">
            Agrega tus cuentas y los pagos importantes. No necesitas todo tu historial.
          </Step>
          <Step number="02" title="Escribe lo que entró o salió">
            Describe una compra, un gasto o un ingreso. Revisa lo que prepara el asistente.
          </Step>
          <Step number="03" title="Pregunta antes de comprar">
            Mira cuánto te queda de tu presupuesto este mes y qué pagos tienes pendientes.
          </Step>
        </div>
      </section>

      <section className="border-y border-[#e8e2d9] bg-white px-5 py-14 sm:px-8 lg:px-[120px] lg:py-24">
        <div className="mx-auto max-w-[1200px]">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="section-title">Tus números, en una sola vista</h2>
              <p className="mt-3 max-w-[630px] text-[17px] leading-7 text-slate-600 sm:text-lg">
                Revisa lo que tienes, lo que debes, tus próximos pagos y lo que podría quedarte al
                terminar el mes.
              </p>
            </div>
            <p className="rounded-xl border border-[#e8e2d9] bg-[#f6f2ec] px-3.5 py-2 font-mono text-xs text-stone-600">
              Datos de ejemplo · MXN
            </p>
          </div>
          <DashboardPreview />
          <div className="mt-6 grid gap-5 lg:grid-cols-2 lg:gap-6">
            <DecisionCard
              eyebrow="Tarjeta y deuda"
              icon="card"
              title="Tu tarjeta de crédito no es dinero disponible"
            >
              Antes de una compra, compara el pago completo o la mensualidad con el dinero que te
              queda después de tus gastos y pagos. El límite del banco puede autorizar un cargo; no
              necesariamente significa que te conviene.
            </DecisionCard>
            <DecisionCard
              eyebrow="Metas y ahorro"
              icon="target"
              title="Que una meta no se coma tu quincena"
            >
              Aparta dinero para tus metas y revisa cuánto puedes destinar sin contar dos veces el
              dinero que necesitas para gastos o tarjeta.
            </DecisionCard>
          </div>
        </div>
      </section>

      {hasPublicPlus && <PlusSection />}

      <section className="bg-slate-900 px-5 py-14 text-white sm:px-8 lg:px-[120px] lg:py-[88px]">
        <div className="mx-auto max-w-[1200px]">
          <h2 className="text-[30px] font-bold leading-[1.15] tracking-[-.03em] sm:text-4xl">
            Tu información y tus decisiones siguen bajo tu control
          </h2>
          <p className="mt-3 max-w-[680px] text-[17px] leading-7 text-slate-300">
            Estas reglas se aplican cada vez que usas el asistente.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:mt-12 lg:grid-cols-3 lg:gap-5">
            <Trust title="Tú decides cuándo usar IA" icon="hand">
              Puedes desactivarla desde Ajustes.
            </Trust>
            <Trust title="La IA recibe sólo lo necesario" icon="eye">
              Recibe la información necesaria para responderte. No tiene acceso libre a todos tus
              datos.
            </Trust>
            <Trust title="Tú confirmas" icon="check">
              El asistente prepara. Nada se registra hasta que confirmas. La app nunca mueve dinero
              en tu banco.
            </Trust>
            <Trust title="No compartas datos bancarios sensibles" icon="card">
              Nunca escribas tu contraseña, NIP, CVV, número completo de tarjeta, clave dinámica ni
              códigos por SMS.
            </Trust>
            <Trust title="No vendemos tus datos" icon="undo">
              Consulta qué información usa la IA, para qué y durante cuánto tiempo.
            </Trust>
          </div>
        </div>
      </section>

      <section className="border-b border-[#e8e2d9] bg-white px-5 py-14 sm:px-8 lg:px-[120px] lg:py-24">
        <div className="mx-auto grid max-w-[1200px] gap-8 lg:grid-cols-[360px_1fr] lg:gap-16">
          <div>
            <h2 className="section-title">Preguntas frecuentes</h2>
            <p className="mt-3 text-[17px] leading-7 text-slate-600">
              Si algo no queda claro, escríbenos antes de registrarte.
            </p>
          </div>
          <div>
            <Faq title="¿Tengo que registrar todo mi historial?" open>
              No. Empieza con los saldos de hoy y completa lo que necesites a tu ritmo.
            </Faq>
            <Faq title="¿Puede guardar una compra sin mi permiso?">
              No. Primero muestra el registro y su efecto. Sólo lo agrega cuando confirmas.
            </Faq>
            <Faq title="¿Se conecta a mi banco?">No. Tampoco mueve dinero ni paga por ti.</Faq>
            <Faq title="¿Qué sigue siendo gratis?">
              Puedes registrar ingresos, gastos y traspasos. También puedes usar tus cuentas,
              compras planeadas, movimientos fijos, tarjeta de crédito, deudas, presupuesto, metas y
              Panorama.
            </Faq>
            <Faq title="¿Cómo funciona la prueba del asistente?">
              Se ofrece una sola vez por cuenta. Después de verificar tu correo, empieza cuando
              aceptas usar IA. Dura 14 días y puedes tener hasta 10 consultas disponibles. Cada
              consulta vuelve a estar disponible 7 días después de usarla, mientras la prueba siga
              activa.
            </Faq>
            <Faq title="¿Cuándo se descuenta una consulta?">
              Cuando recibes el análisis final, no mientras completas los datos. Preparar, revisar,
              confirmar o cancelar un registro tampoco descuenta consultas. Necesitas una prueba
              activa o una suscripción.
            </Faq>
            <Faq title="¿La app garantiza que puedo comprar algo?">
              No. Muestra una estimación con lo que registraste y confirmaste. Tú decides.
            </Faq>
            <Faq title="¿Cómo usa mis datos la IA?">
              Te decimos qué servicio de IA usamos, qué información enviamos, para qué, cuánto
              tiempo se guarda y cómo retirar el permiso.
            </Faq>
            {hasPublicPlus && (
              <Faq title="¿Qué incluye Plus hoy?">
                Las capacidades marcadas como disponibles en la oferta vigente.
              </Faq>
            )}
            {hasPublicPlus && (
              <Faq title="¿Cómo se cobra y cómo cancelo?">
                Plus es mensual y se renueva hasta que lo canceles desde Ajustes. Conservas el
                acceso hasta terminar el periodo que ya pagaste.
              </Faq>
            )}
            {hasPublicPlus && (
              <Faq title="¿Qué pasa si dejo de pagar?">
                Conservas tus datos y todas las funciones gratuitas.
              </Faq>
            )}
          </div>
        </div>
      </section>

      <section className="px-5 py-16 text-center sm:px-8 lg:px-[120px] lg:py-[104px]">
        <h2 className="mx-auto max-w-[760px] text-[34px] font-bold leading-[1.1] tracking-[-.035em] sm:text-[44px]">
          Cuéntale lo que gastaste. Pregunta antes de gastar.
        </h2>
        <p className="mx-auto mt-4 max-w-[560px] text-[17px] leading-7 text-slate-600 sm:text-[19px]">
          Empieza con tus saldos de hoy, no con años de historial. Tú siempre decides.
        </p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link
            to="/registro"
            className="button button-primary min-h-14 rounded-[14px] px-7 text-[17px]"
          >
            Probar gratis
          </Link>
          <Link
            to="/login"
            className="button button-secondary min-h-14 rounded-[14px] border-[#dcd5ca] px-6 text-[17px]"
          >
            Iniciar sesión
          </Link>
        </div>
        <p className="mt-5 text-sm font-semibold text-green-700">
          No necesitas tarjeta bancaria para registrarte. Cuando termine tu prueba, puedes seguir
          usando Gratis sin pagar.
        </p>
      </section>

      <footer className="border-t border-[#e8e2d9] bg-[#f6f2ec] px-5 py-8 pb-[calc(2rem+env(safe-area-inset-bottom,0px))] sm:px-8 lg:px-[120px] lg:py-12">
        <div className="mx-auto flex max-w-[1200px] flex-col justify-between gap-8 border-b border-[#e8e2d9] pb-7 sm:flex-row">
          <div className="max-w-sm">
            <Brand />
            <p className="mt-4 text-sm leading-6 text-stone-600">
              App de finanzas personales hecha en México. Los cálculos orientan tus decisiones; no
              sustituyen tu estado de cuenta ni asesoría fiscal o de inversión.
            </p>
          </div>
          <nav aria-label="Enlaces del pie" className="grid grid-cols-2 content-start gap-x-8">
            <Link to="/privacidad" className="footer-link">
              Aviso de privacidad
            </Link>
            <Link to="/login" className="footer-link">
              Iniciar sesión
            </Link>
            <Link to="/registro" className="footer-link">
              Crear cuenta gratis
            </Link>
            <Link to="/soporte" className="footer-link">
              Soporte
            </Link>
          </nav>
        </div>
        <p className="mx-auto mt-5 max-w-[1200px] text-xs text-stone-600">
          © 2026 Asistente Financiero · Hecho en México · Cifras de ejemplo
        </p>
      </footer>

      <div className="fixed inset-x-0 bottom-0 z-10 flex items-center gap-3 border-t border-[#e8e2d9] bg-[#faf8f5]/95 px-5 py-2.5 pb-[calc(.625rem+env(safe-area-inset-bottom))] backdrop-blur md:hidden">
        <span className="min-w-0 flex-1 text-xs leading-4 text-stone-600">Gratis, sin tarjeta</span>
        <Link to="/registro" className="button button-primary min-h-11 shrink-0 rounded-xl px-5">
          Crear cuenta gratis
        </Link>
      </div>
    </main>
  );
}

function Brand() {
  return (
    <Link to="/" className="tap-target flex items-center gap-2.5 text-slate-900">
      <span className="flex h-9 w-9 items-center justify-center rounded-[11px] bg-slate-900 text-white">
        <Icon name="chart" className="h-5 w-5" />
      </span>
      <span className="text-sm font-bold tracking-tight sm:text-base">Asistente Financiero</span>
    </Link>
  );
}

function TrustNote({ children }: { children: string }) {
  return (
    <li className="flex items-start gap-1.5">
      <Icon name="check" className="mt-0.5 h-4 w-4 shrink-0" />
      {children}
    </li>
  );
}

function Nav({ href, children }: { href: string; children: string }) {
  return (
    <a href={href} className="nav-link">
      {children}
    </a>
  );
}

function HeroPreview() {
  return (
    <div className="space-y-4" aria-label="Ejemplo ilustrativo de un panorama financiero">
      <div className="rounded-[20px] bg-slate-900 p-6 shadow-[0_24px_48px_-26px_rgba(15,23,42,.75)] sm:p-7">
        <p className="text-xs font-semibold uppercase tracking-[.06em] text-slate-400">
          Te queda de tu límite de gasto este mes · Estimado
        </p>
        <div className="mt-3 flex items-end justify-between gap-4">
          <strong className="text-4xl tracking-[-.035em] text-white tabular-nums sm:text-5xl">
            $3,130
          </strong>
          <p className="text-right text-xs leading-5 text-slate-300">
            de $11,250 del mes
            <br />
            <span className="text-slate-400">17 días restantes</span>
          </p>
        </div>
        <Progress value="72%" color="bg-green-600" className="mt-5 bg-slate-800" />
        <div className="mt-2 flex justify-between gap-3 text-[11px] text-slate-400 sm:text-xs">
          <span>Gastado $8,120 · 72%</span>
          <span>$184 al día para no pasarte</span>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 border-t border-slate-700 pt-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[.06em] text-slate-400">
              Deuda actual
            </p>
            <strong className="mt-1 block text-lg tabular-nums text-white">$8,250</strong>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[.06em] text-slate-400">
              Próximo pago
            </p>
            <strong className="mt-1 block text-lg text-white">22 sep</strong>
          </div>
        </div>
      </div>
      <div className="rounded-[20px] border border-[#e8e2d9] bg-white p-5 shadow-[0_16px_32px_-26px_rgba(15,23,42,.35)] sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-bold">Próximos pagos</h2>
          <span className="text-xs text-slate-500">Siguientes 14 días</span>
        </div>
        <Payment
          title="Pago de tarjeta"
          date="Vence el 22 de septiembre"
          amount="$8,250"
          icon="card"
          amber
        />
        <Payment title="Renta" date="Vence el 1 de octubre" amount="$9,800" icon="calendar" />
        <Payment
          title="Colegiatura"
          date="Vence el 3 de octubre"
          amount="$4,300"
          icon="swap"
          last
        />
      </div>
      <p className="font-mono text-xs text-slate-600">Datos de ejemplo · MXN</p>
    </div>
  );
}

function AssistantShowcase() {
  const statusClass = canUseAssistant
    ? 'border-green-200 bg-green-50 text-green-700'
    : 'border-amber-200 bg-amber-50 text-amber-800';

  return (
    <section
      id="asistente"
      aria-labelledby="assistant-showcase-title"
      className="border-y border-[#e8e2d9] bg-slate-900 px-5 py-14 text-white sm:px-8 lg:px-[120px] lg:py-24"
    >
      <div className="mx-auto grid max-w-[1200px] gap-10 lg:grid-cols-[minmax(0,430px)_minmax(0,1fr)] lg:items-center lg:gap-16">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-mono text-xs font-semibold uppercase tracking-[.16em] text-green-400">
              Tu asistente personal
            </p>
            <span className={'rounded-full border px-3 py-1 text-xs font-bold ' + statusClass}>
              {assistantStatus}
            </span>
          </div>
          <h2
            id="assistant-showcase-title"
            className="mt-5 text-[32px] font-bold leading-[1.08] tracking-[-.035em] sm:text-[44px]"
          >
            Tú cuentas la compra. El asistente prepara lo demás.
          </h2>
          <p className="mt-5 text-[17px] leading-7 text-slate-300">
            {canUseAssistant
              ? 'Escribe una compra como la contarías. El asistente completa lo necesario, muestra el efecto en tus números y espera tu confirmación.'
              : 'Antes de comprar, revisa cómo afectaría tu mes. Compara esa compra con tu presupuesto, tus pagos registrados y lo que podría quedarte al terminar el mes.'}
          </p>
          <ol className="mt-8 space-y-5">
            <ShowcasePoint number="1" title="Dilo con tus palabras">
              Incluye lo que recuerdes: qué compraste, cuánto pagaste y cuándo.
            </ShowcasePoint>
            <ShowcasePoint number="2" title="Revisa el registro">
              Completa la tarjeta, la forma de pago y otros datos que ya conoce. Si falta algo,
              pregunta.
            </ShowcasePoint>
            <ShowcasePoint number="3" title="Decide con tus números">
              Pregunta cuánto te queda este mes o si una compra cabe en tu presupuesto.
            </ShowcasePoint>
            <ShowcasePoint number="4" title="Tú das la última palabra">
              Nada se guarda hasta que confirmas. La app nunca mueve dinero en tu banco.
            </ShowcasePoint>
          </ol>
          <div className="mt-8">
            <Link
              to="/registro"
              className="button min-h-12 rounded-xl bg-green-400 px-5 font-bold text-slate-950 hover:bg-green-300"
            >
              Probar el asistente gratis
            </Link>
            <p className="mt-3 max-w-[410px] text-xs leading-5 text-slate-400">
              La prueba dura 14 días y se ofrece una vez por cuenta. Empieza cuando, después de
              verificar tu correo, aceptas usar la IA por primera vez.
            </p>
          </div>
        </div>

        <figure className="overflow-hidden rounded-[22px] border border-slate-700 bg-[#faf8f5] text-slate-900 shadow-[0_30px_70px_-36px_rgba(0,0,0,.85)]">
          <div className="flex items-center justify-between gap-3 border-b border-[#e8e2d9] bg-white px-4 py-3 sm:px-6">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-green-400">
                <Icon name="chart" className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-bold">Mi asistente personal</p>
                <p className="text-[11px] text-slate-500">Demostración con datos ficticios</p>
              </div>
            </div>
            <span
              className={
                'shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[.06em] ' +
                statusClass
              }
            >
              {assistantStatus}
            </span>
          </div>
          <div className="space-y-4 p-4 sm:p-6">
            <div className="ml-auto max-w-[88%] rounded-2xl rounded-br-sm bg-slate-900 px-4 py-3 text-sm leading-6 text-white sm:max-w-[78%]">
              Registra esta compra: ayer gasté $120 en comida rápida con mi tarjeta de crédito.
            </div>
            <div className="flex max-w-[92%] items-start gap-2.5">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-green-400">
                <Icon name="check" className="h-4 w-4" />
              </span>
              <p className="rounded-2xl rounded-tl-sm border border-[#e8e2d9] bg-white px-4 py-3 text-sm leading-6 text-slate-700">
                Preparé el registro con lo que me dijiste y los datos de tu tarjeta. Revisa antes de
                guardarlo.
              </p>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
                <strong className="text-sm">Compra lista para revisar</strong>
                <span className="rounded-md bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[.05em] text-amber-800">
                  No guardada
                </span>
              </div>
              <div className="grid gap-x-5 gap-y-4 p-4 sm:grid-cols-2 sm:p-5">
                <PreviewField label="Compra" value="Comida rápida" />
                <PreviewField label="Precio" value="$120.00 MXN" />
                <PreviewField label="Fecha" value="Ayer" />
                <PreviewField label="Forma de pago" value="Un solo pago" />
                <PreviewField label="Tarjeta de crédito" value="Tarjeta Azul" />
                <div>
                  <p className="text-xs text-slate-500">Categoría sugerida</p>
                  <p className="mt-1 font-bold">Restaurantes</p>
                  <p className="mt-1 inline-flex rounded-md bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700">
                    Se creará al guardar
                  </p>
                </div>
              </div>
              <div className="mx-4 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-950 sm:mx-5">
                <strong className="block">Así cambiaría tu tarjeta</strong>
                <p className="mt-1 tabular-nums">
                  Deuda: $8,250.00 → <strong>$8,370.00</strong>
                </p>
              </div>
              <div className="mt-4 border-t border-slate-100 p-4 sm:flex sm:items-center sm:justify-between sm:gap-4 sm:px-5">
                <p className="text-xs leading-5 text-slate-500">
                  La categoría y la compra se crearían juntas sólo después de confirmar.
                </p>
                <div
                  aria-label="Acciones ilustrativas de la propuesta"
                  className="mt-3 flex gap-2 sm:mt-0"
                >
                  <span className="inline-flex min-h-11 items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-bold text-white">
                    Guardar compra
                  </span>
                  <span className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-200 px-4 text-sm font-semibold">
                    No guardar
                  </span>
                </div>
              </div>
            </div>
            <p className="text-xs leading-5 text-slate-600">
              Interpretar el mensaje inicial usa 1 consulta. Guardar o descartar la propuesta no
              usa otra. Necesitas una prueba activa o una suscripción.
            </p>
          </div>
          <figcaption className="sr-only">
            {canUseAssistant
              ? 'Ejemplo del flujo disponible: el usuario describe una compra, el asistente prepara sus datos, propone crear la categoría Restaurantes, muestra el efecto en la deuda y espera confirmación.'
              : 'Demostración de un flujo planeado: el usuario describe una compra, revisa sus datos y confirma antes de guardarla.'}
          </figcaption>
        </figure>
        <PurchaseDecisionPreview />
      </div>
    </section>
  );
}

function PurchaseDecisionPreview() {
  return (
    <figure className="overflow-hidden rounded-[22px] border border-slate-700 bg-[#faf8f5] text-slate-900 shadow-[0_30px_70px_-36px_rgba(0,0,0,.85)] lg:col-span-2">
      <div className="border-b border-[#e8e2d9] bg-white px-5 py-4 sm:px-6">
        <p className="font-mono text-xs font-semibold uppercase tracking-[.1em] text-slate-600">
          Antes de comprar
        </p>
        <h3 className="mt-1 text-lg font-bold">Revisa si una compra cabe en tu mes</h3>
      </div>
      <div className="grid gap-5 p-5 sm:p-6 lg:grid-cols-[minmax(0,340px)_1fr] lg:items-start">
        <div>
          <div className="rounded-2xl rounded-br-sm bg-slate-900 px-4 py-3 text-sm leading-6 text-white">
            Quiero comprar una laptop de $18,000 a 12 meses sin intereses. ¿Puedo pagarla sin
            quedarme corto para mis próximos pagos?
          </div>
          <div className="mt-4 rounded-2xl border border-[#e8e2d9] bg-white px-4 py-3 text-sm leading-6 text-slate-700">
            También puedo ayudarte a pensar para qué la quieres y qué otras opciones tienes.
          </div>
        </div>
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
            <strong className="text-sm">Efecto estimado de la compra</strong>
            <span className="rounded-md bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[.05em] text-amber-800">
              Queda justo
            </span>
          </div>
          <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
            <DecisionMetric label="Pago al mes" value="$1,500" detail="12 meses sin intereses" />
            <DecisionMetric
              label="Deuda de la tarjeta"
              value="$8,250 → $26,250"
              detail="Incluye los $18,000 de la compra"
            />
            <DecisionMetric
              label="Presupuesto restante"
              value="$3,130 → $1,630"
              detail="Para lo que falta del mes"
            />
            <DecisionMetric
              label="Crédito disponible"
              value="$24,000 → $6,000"
              detail="Dato separado de tu presupuesto"
            />
          </div>
          <div className="border-t border-slate-100 px-4 py-3 text-xs leading-5 text-slate-500 sm:px-5">
            <strong className="text-slate-700">Pagos considerados:</strong> tarjeta, renta y
            colegiatura.
          </div>
          <div className="border-t border-slate-100 bg-blue-50 px-4 py-3 text-xs font-semibold leading-5 text-blue-900 sm:px-5">
            Estimación con datos de ejemplo. No es garantía.
          </div>
        </div>
      </div>
      <figcaption className="sr-only">
        Ejemplo de una consulta antes de comprar, con pago mensual, deuda, presupuesto, crédito
        disponible y pagos considerados.
      </figcaption>
    </figure>
  );
}

function DecisionMetric({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <strong className="mt-1 block text-lg tracking-tight tabular-nums">{value}</strong>
      <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  );
}

function ShowcasePoint({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: string;
}) {
  return (
    <li className="flex gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-600 bg-slate-800 font-mono text-xs font-bold text-green-400">
        {number}
      </span>
      <div>
        <h3 className="font-bold text-white">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-slate-300">{children}</p>
      </div>
    </li>
  );
}

function PreviewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 font-bold">{value}</p>
    </div>
  );
}

function Benefit({
  icon,
  tone,
  title,
  children,
}: {
  icon: IconName;
  tone: 'green' | 'amber' | 'blue';
  title: string;
  children: string;
}) {
  const toneClass =
    tone === 'green'
      ? 'bg-green-50 text-green-700'
      : tone === 'amber'
        ? 'bg-amber-50 text-amber-700'
        : 'bg-blue-50 text-blue-700';
  return (
    <article className="rounded-[18px] border border-[#e8e2d9] bg-[#faf8f5] p-6 sm:p-8">
      <span className={'flex h-12 w-12 items-center justify-center rounded-[14px] ' + toneClass}>
        <Icon name={icon} className="h-6 w-6" />
      </span>
      <h3 className="mt-5 text-xl font-bold tracking-[-.02em] sm:text-[22px]">{title}</h3>
      <p className="mt-2 text-base leading-7 text-slate-600">{children}</p>
    </article>
  );
}

function DecisionCard({
  eyebrow,
  icon,
  title,
  children,
}: {
  eyebrow: string;
  icon: IconName;
  title: string;
  children: string;
}) {
  return (
    <article className="rounded-[18px] border border-[#e8e2d9] bg-[#faf8f5] p-6 sm:p-7">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-green-400">
          <Icon name={icon} className="h-5 w-5" />
        </span>
        <p className="font-mono text-xs font-semibold uppercase tracking-[.1em] text-slate-600">
          {eyebrow}
        </p>
      </div>
      <h3 className="mt-5 text-xl font-bold tracking-[-.02em] sm:text-[22px]">{title}</h3>
      <p className="mt-2 text-base leading-7 text-slate-600">{children}</p>
    </article>
  );
}

function Step({
  number,
  optional = false,
  title,
  children,
}: {
  number: string;
  optional?: boolean;
  title: string;
  children: string;
}) {
  return (
    <article
      className={
        optional
          ? 'rounded-[18px] border border-dashed border-[#cfc6b8] bg-[#f6f2ec] p-6 sm:p-8'
          : 'rounded-[18px] border border-[#e8e2d9] bg-white p-6 shadow-sm sm:p-8'
      }
    >
      <div className="flex items-center gap-2.5">
        <span className="font-mono text-xs font-semibold tracking-[.1em] text-green-700">
          PASO {number}
        </span>
        {optional && (
          <span className="rounded-md bg-[#e7e1d6] px-2 py-1 text-[11px] font-bold uppercase tracking-[.04em] text-stone-600">
            Opcional
          </span>
        )}
      </div>
      <h3 className="mt-5 text-[22px] font-bold leading-7 tracking-[-.025em] sm:text-2xl">
        {title}
      </h3>
      <p className="mt-2 text-base leading-7 text-slate-600">{children}</p>
    </article>
  );
}

function DashboardPreview() {
  return (
    <div
      className="mt-9 overflow-hidden rounded-[20px] border border-slate-200 bg-slate-50 shadow-[0_32px_64px_-40px_rgba(15,23,42,.45)]"
      aria-label="Maqueta estática de la pantalla de panorama con datos ficticios"
    >
      <div className="flex h-11 items-center gap-2 border-b border-slate-200 bg-slate-100 px-4">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="ml-3 font-mono text-xs text-slate-600">finanzas.ejemplo.mx · panel</span>
      </div>
      <div className="grid lg:grid-cols-[212px_1fr]">
        <aside className="hidden border-r border-slate-200 bg-white p-4 lg:block">
          <div className="mb-4 flex items-center gap-2 text-sm font-bold">
            <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-slate-900 text-white">
              <Icon name="chart" className="h-4 w-4" />
            </span>
            Asistente Financiero
          </div>
          <div className="rounded-xl bg-slate-900 px-3 py-3 text-sm font-semibold text-white">
            Panorama
          </div>
          <div className="px-3 py-3 text-sm text-slate-600">Cuentas</div>
          <div className="px-3 py-3 text-sm text-slate-600">Movimientos</div>
          <div className="px-3 py-3 text-sm text-slate-600">Tarjeta</div>
          <div className="px-3 py-3 text-sm text-slate-600">Metas</div>
        </aside>
        <div className="p-4 sm:p-6">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.09em] text-slate-600">
                Hola, Renata
              </p>
              <h3 className="mt-0.5 text-xl font-bold tracking-tight">Tu panorama</h3>
            </div>
            <span className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold">
              Sep 2026
            </span>
          </div>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Metric label="Dinero en tus cuentas" value="$142,300" />
            <Metric label="Al terminar el mes · Estimado" value="$136,610" green />
            <Metric label="Deuda registrada" value="$8,250" />
            <Metric label="Patrimonio" value="$134,050" green />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <DashboardCard title="Presupuesto del mes">
              <p className="text-xs text-slate-500">Gasto variable · límite $11,250</p>
              <div className="mt-3 flex items-baseline gap-2">
                <strong className="text-3xl tracking-tight tabular-nums">$8,120</strong>
                <span className="text-xs text-slate-500">gastados · 72%</span>
              </div>
              <Progress value="72%" color="bg-amber-600" className="mt-3" />
              <Mini label="Supermercado" value="$3,180" width="62%" />
              <Mini label="Restaurantes" value="$1,640" width="88%" amber />
              <Mini label="Transporte" value="$980" width="34%" />
            </DashboardCard>
            <DashboardCard title="Próximos pagos" hint="$22,350 en 14 días">
              <Payment
                title="Pago de tarjeta"
                date="22 sep · pago programado"
                amount="$8,250"
                icon="card"
                small
                amber
              />
              <Payment
                title="Renta"
                date="1 oct · fijo mensual"
                amount="$9,800"
                icon="calendar"
                small
              />
              <Payment
                title="Colegiatura"
                date="3 oct · fijo mensual"
                amount="$4,300"
                icon="swap"
                small
                last
              />
            </DashboardCard>
            <DashboardCard title="Metas de ahorro" hint="2 activas">
              <Goal
                title="Enganche de la casa"
                amount="$80,000 de $300,000"
                value="27%"
                color="bg-blue-600"
                detail="27% · diciembre 2028"
              />
              <Goal
                title="Fondo para imprevistos"
                amount="$34,500 de $60,000"
                value="57.5%"
                color="bg-green-600"
                detail="58% · aportas $2,500 al mes"
              />
            </DashboardCard>
            <DashboardCard title="Tarjeta de crédito" hint="Corte el 18 sep">
              <div className="grid grid-cols-2 gap-3">
                <Metric label="Deuda actual" value="$8,250" />
                <Metric label="Tope personal de compra" value="$2,900" green />
              </div>
              <p className="mt-3 rounded-xl bg-slate-100 p-3 text-xs leading-5 text-slate-700">
                <strong>Próximo pago registrado: $8,250.</strong> Consulta en tu estado de cuenta el
                pago para no generar intereses y la fecha exacta.
              </p>
            </DashboardCard>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlusSection() {
  return (
    <section id="plus" className="mx-auto max-w-[1440px] px-5 py-14 sm:px-8 lg:px-[120px] lg:py-24">
      <div className="grid gap-9 lg:grid-cols-[400px_1fr] lg:gap-16">
        <div>
          <p className="font-mono text-xs font-semibold uppercase tracking-[.16em] text-slate-600">
            Plus · opcional
          </p>
          <h2 className="mt-4 section-title">Paga sólo si quieres ahorrarte trabajo</h2>
          <p className="mt-4 text-lg leading-7 text-slate-600">
            Lo esencial es gratis y sin límite. Plus cubre el uso del asistente.
          </p>
          <div className="mt-6 flex items-baseline gap-2 rounded-2xl bg-slate-900 px-5 py-5 text-white">
            <strong className="text-3xl tracking-tight">{publicOffer.priceMxn}</strong>
            <span className="text-sm text-slate-300">MXN / mes · IVA incluido</span>
          </div>
          <Link
            to="/registro"
            className="button button-secondary mt-6 min-h-[52px] rounded-[14px] border-[#dcd5ca] px-6"
          >
            Empieza gratis y decide después
          </Link>
        </div>
        <div className="space-y-4">
          {publicOffer.available.length > 0 && (
            <Offer
              title="Incluido hoy en Plus"
              label="Disponible"
              items={publicOffer.available}
              tone="green"
            />
          )}
          {publicOffer.beta.length > 0 && (
            <Offer
              title="En prueba, puedes activarlas o no"
              label="Beta"
              items={publicOffer.beta}
              tone="blue"
            />
          )}
          {publicOffer.comingSoon.length > 0 && (
            <Offer
              title="No forma parte de lo que pagas"
              label="Próximamente"
              items={publicOffer.comingSoon}
              tone="stone"
            />
          )}
          <div className="rounded-[18px] border border-[#e8e2d9] bg-white p-6 text-sm leading-6 text-slate-700">
            <p>
              <strong>
                Plus incluye hasta {publicOffer.assistantQueries} consultas disponibles.
              </strong>{' '}
              Cada consulta vuelve a estar disponible {publicOffer.queryReturnDays} días después de
              usarla.
            </p>
            <p className="mt-3">
              <strong>Nada se aplica solo.</strong> Cada acción se muestra completa y se aplica
              únicamente si tú la confirmas.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Trust({ title, icon, children }: { title: string; icon: IconName; children: string }) {
  return (
    <article className="rounded-[18px] bg-slate-800 p-6">
      <span className="flex h-[42px] w-[42px] items-center justify-center rounded-xl bg-slate-900 text-green-400">
        <Icon name={icon} className="h-5 w-5" />
      </span>
      <h3 className="mt-4 text-base font-bold">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-300">{children}</p>
    </article>
  );
}

function Faq({
  title,
  children,
  open = false,
}: {
  title: string;
  children: ReactNode;
  open?: boolean;
}) {
  return (
    <details open={open} className="border-t border-[#e8e2d9] last:border-b">
      <summary className="flex min-h-16 cursor-pointer items-center justify-between gap-4 px-1 py-3 text-base font-semibold marker:content-none sm:text-lg">
        {title}
        <Icon name="down" className="h-5 w-5 shrink-0 text-slate-500" />
      </summary>
      <p className="max-w-[700px] px-1 pb-5 text-sm leading-7 text-slate-600 sm:text-base">
        {children}
      </p>
    </details>
  );
}

function Payment({
  title,
  date,
  amount,
  icon,
  amber = false,
  last = false,
  small = false,
}: {
  title: string;
  date: string;
  amount: string;
  icon: IconName;
  amber?: boolean;
  last?: boolean;
  small?: boolean;
}) {
  return (
    <div
      className={
        last
          ? 'flex items-center gap-3 py-3'
          : 'flex items-center gap-3 border-b border-slate-100 py-3'
      }
    >
      <span
        className={
          (small ? 'h-8 w-8 ' : 'h-[38px] w-[38px] ') +
          (amber ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-700') +
          ' flex shrink-0 items-center justify-center rounded-xl'
        }
      >
        <Icon name={icon} className={small ? 'h-4 w-4' : 'h-5 w-5'} />
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block truncate text-sm">{title}</strong>
        <span className="block text-xs text-slate-500">{date}</span>
      </span>
      <strong className={(small ? 'text-sm' : 'text-base') + ' tabular-nums'}>{amount}</strong>
    </div>
  );
}

function DashboardCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold">{title}</h3>
        {hint && <span className="text-xs text-slate-500">{hint}</span>}
      </div>
      <div className="mt-2">{children}</div>
    </article>
  );
}

function Progress({
  value,
  color,
  className = '',
}: {
  value: string;
  color: string;
  className?: string;
}) {
  return (
    <span className={'block h-2 overflow-hidden rounded-full bg-slate-100 ' + className}>
      <span className={'block h-full rounded-full ' + color} style={{ width: value }} />
    </span>
  );
}

function Mini({
  label,
  value,
  width,
  amber = false,
}: {
  label: string;
  value: string;
  width: string;
  amber?: boolean;
}) {
  return (
    <div className="mt-3 flex items-center gap-2 text-xs">
      <span className="min-w-0 flex-1 truncate text-slate-600">{label}</span>
      <span className="h-1.5 w-20 overflow-hidden rounded bg-slate-100">
        <span
          className={(amber ? 'bg-amber-600' : 'bg-green-600') + ' block h-full rounded'}
          style={{ width }}
        />
      </span>
      <strong className="w-12 text-right tabular-nums">{value}</strong>
    </div>
  );
}

function Goal({
  title,
  amount,
  value,
  color,
  detail,
}: {
  title: string;
  amount: string;
  value: string;
  color: string;
  detail: string;
}) {
  return (
    <div className="mt-4 first:mt-0">
      <div className="flex justify-between gap-3 text-xs">
        <strong>{title}</strong>
        <span className="text-right text-slate-500">{amount}</span>
      </div>
      <Progress value={value} color={color} className="mt-2" />
      <p className="mt-1 text-[11px] text-slate-500">{detail}</p>
    </div>
  );
}

function Metric({
  label,
  value,
  green = false,
}: {
  label: string;
  value: string;
  green?: boolean;
}) {
  return (
    <div className={green ? 'rounded-xl bg-green-50 p-3' : 'rounded-xl bg-slate-50 p-3'}>
      <p
        className={
          (green ? 'text-green-700' : 'text-slate-500') +
          ' text-[10px] font-bold uppercase tracking-[.05em]'
        }
      >
        {label}
      </p>
      <strong
        className={
          (green ? 'text-green-700' : 'text-slate-900') +
          ' mt-1 block text-lg tracking-tight tabular-nums'
        }
      >
        {value}
      </strong>
    </div>
  );
}

function Offer({
  title,
  label,
  items,
  tone,
}: {
  title: string;
  label: string;
  items: string[];
  tone: 'green' | 'blue' | 'stone';
}) {
  const toneClass =
    tone === 'green'
      ? 'border-green-200 bg-green-50 text-green-700'
      : tone === 'blue'
        ? 'border-blue-200 bg-blue-50 text-blue-700'
        : 'border-[#cfc6b8] bg-[#f6f2ec] text-stone-700';
  return (
    <article className={'rounded-[18px] border p-6 ' + toneClass}>
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-white/70 px-2 py-1 text-[11px] font-bold uppercase tracking-[.04em]">
          {label}
        </span>
        <h3 className="font-bold text-slate-900">{title}</h3>
      </div>
      <ul className="mt-4 space-y-2 text-sm leading-6">
        {items.map((item) => (
          <li key={item}>• {item}</li>
        ))}
      </ul>
    </article>
  );
}

type IconName =
  | 'chart'
  | 'wallet'
  | 'calendar'
  | 'target'
  | 'card'
  | 'swap'
  | 'check'
  | 'down'
  | 'hand'
  | 'eye'
  | 'undo';

function Icon({ name, className }: { name: IconName; className?: string }) {
  const paths: Record<IconName, ReactNode> = {
    chart: (
      <>
        <path d="M4 19V5M4 19h16" />
        <path d="m7 14 4-4 3 2 5-6" />
      </>
    ),
    wallet: (
      <>
        <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H19v14H6.5A2.5 2.5 0 0 1 4 16.5v-9Z" />
        <path d="M4 8h13v4H4" />
        <path d="M15 14h.01" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M16 3v4M8 3v4M3 10h18" />
      </>
    ),
    target: (
      <>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="3" />
        <path d="m15 9 6-6" />
      </>
    ),
    card: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 10h18M7 15h3" />
      </>
    ),
    swap: (
      <>
        <path d="M7 7h11l-3-3M18 17H7l3 3M18 7l3 3M7 17l-3-3" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    down: <path d="m6 9 6 6 6-6" />,
    hand: (
      <>
        <path d="M8 12V5a1.5 1.5 0 0 1 3 0v5" />
        <path d="M11 10V4a1.5 1.5 0 0 1 3 0v6M14 11V6a1.5 1.5 0 0 1 3 0v6" />
        <path d="m8 10-1-1a1.7 1.7 0 0 0-2.4 2.4l4.3 4.3A5 5 0 0 0 12.4 17H15a5 5 0 0 0 5-5v-2a1.5 1.5 0 0 0-3 0" />
      </>
    ),
    eye: (
      <>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
        <circle cx="12" cy="12" r="2.5" />
      </>
    ),
    undo: (
      <>
        <path d="M8 7 4 11l4 4" />
        <path d="M5 11h10a5 5 0 1 1 0 10h-2" />
      </>
    ),
  };
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}
